import React, { useEffect } from 'react'
import useState from 'react-usestateref';
import './BotDemo.scss'
import ChatHistory from './sub-components/ChatHistory/ChatHistory';
import * as ChatBotAPI from '../../api/ChatBot';

export default function BotDemo() {
    // State for sending status, user text input, and chat history
    const [is_sending, setIs_sending] = React.useState(false)
    const [input_text, setInput_text] = React.useState("")
    const [chat_history, setChat_history, chat_history_ref] = useState([])

    // Session ID from server
    const [session_id, setSession_id] = useState(null)

    // Image input (optional)
    const [input_image, setInput_image, input_image_ref] = useState(null)
    const input_image_button_ref = React.useRef(null)

    // Bot Info (like in the deprecated version)
    const bot_name = 'Astra @ ATROBOT'     
    const logo = 'https://www.atrobot.asia/logo.jpg'

    // On component mount, load session and any existing chat history
    useEffect(() => {
        // Attempt to read session_id from localStorage
        const session_id_local = localStorage.getItem('session_id')
        if (!session_id_local || session_id_local === 'undefined') {
            ChatBotAPI.getSession().then((response) => {
                const session_id_received = response.data.data
                localStorage.setItem('session_id', session_id_received)
                setSession_id(session_id_received)
            })
        } else {
            setSession_id(session_id_local)
            // Load saved chat history
            const chat_history_local = localStorage.getItem('chat_history')
            if (chat_history_local && chat_history_local !== 'undefined') {
                setChat_history(JSON.parse(chat_history_local))
            }
        }

        // If you have a "getTiis" function in ChatBotAPI to fetch knowledge items:
        // ChatBotAPI.getTiis().then((res) => {
        //     setTiis(res.data.data)
        // })
    }, [])

    // Whenever chat_history changes, persist it
    useEffect(() => {
        if (chat_history.length > 0) {
            localStorage.setItem('chat_history', JSON.stringify(chat_history))
        }
    }, [chat_history])

    // Helper to add a message to the chat history
    const AddText = (name, text, isMe, avatar) => {
        setChat_history([
            ...chat_history_ref.current,
            {
                message: text,
                name: name,
                time: new Date().toLocaleTimeString(),
                isMe: isMe,
                logo: avatar,
            },
        ])
    }

    // Clear out chat history (and localStorage copy), then request new session
    const clearChat = () => {
        setChat_history([])
        localStorage.setItem('chat_history', JSON.stringify([]))

        ChatBotAPI.getSession().then((response) => {
            const session_id_received = response.data.data
            localStorage.setItem('session_id', session_id_received)
            setSession_id(session_id_received)
        })
    }

    // Submit question + optional image to the AI
    const askAI = () => {
        if (is_sending || (!input_text && !input_image)) return

        setIs_sending(true)

        // Add user text to chat
        if (input_text) {
            AddText("You", input_text, true, "https://www.w3schools.com/howto/img_avatar.png")
        }

        // If user attached an image, show that as well
        if (input_image) {
            AddText("You", "(Image attached)", true, "https://www.w3schools.com/howto/img_avatar.png")
        }

        // Clear input fields
        const tempText = input_text
        setInput_text("")

        // If there's an image, convert it to base64 and then call the API
        if (input_image) {
            const reader = new FileReader()
            reader.readAsDataURL(input_image)
            reader.onload = () => {
                const base64Image = reader.result
                ChatBotAPI.Ask(tempText, session_id, base64Image)
                    .then((response) => {
                        handleBotResponse(response.data.data)
                    })
                    .catch(handleBotError)
                    .finally(() => {
                        setIs_sending(false)
                        setInput_image(null)
                    })
            }
        } else {
            // Otherwise, just send text
            ChatBotAPI.Ask(tempText, session_id)
                .then((response) => {
                    handleBotResponse(response.data.data)
                })
                .catch(handleBotError)
                .finally(() => {
                    setIs_sending(false)
                })
        }
    }

    // Helper to handle normal bot response
    const handleBotResponse = (text) => {
        if (text) {
            AddText(bot_name, text, false, logo)
        }
    }

    // Helper to handle bot errors
    const handleBotError = (err) => {
        AddText(bot_name, "ขออภัยมีข้อผิดพลาดเกิดขึ้น โปรดลองใหม่ในภายหลังครับ", false, logo)
        console.error(err)
    }

    return (
        <div className='bot-demo-page'>
            <div className='bot-demo-container'>
                {/* Left panel: main chat UI */}
                <div className="bot-demo-left">
                    <div className="container">
                        {/* Header with logo + bot name */}
                        <div className="title">
                            <div className="logo">
                                <img src={logo} alt="bot-logo" />
                            </div>
                            <div className="name">{bot_name}</div>
                        </div>

                        {/* Chat History or Placeholder */}
                        <div className="chat-history">
                            {chat_history.length === 0 ? (
                                <div className="placeholder">
                                    <p>How can I help you today? Try asking:</p>
                                    <ul>
                                        <li>How can I issue the tax invoice?</li>
                                        <li>What components should I use for making a balancing robot?</li>
                                        <li>I need it urgently; can I pick it up at the store?</li>
                                        <li>Where can I buy your product?</li>
                                        <li>Or /Talk to people</li>
                                    </ul>
                                </div>
                            ) : (
                                <ChatHistory
                                    chat_history={chat_history}
                                    setChat_history={setChat_history}
                                />
                            )}
                        </div>

                        {/* Input area + send/clear buttons */}
                        <div className="user-action">
                            {/* Clear chat */}
                            <div
                                className="input-wrapper-input-icon"
                                onClick={clearChat}
                                style={{ cursor: 'pointer' }}
                            >
                                <i className="fa-solid fa-trash"></i>
                            </div>

                            {/* Text input */}
                            <input
                                type="text"
                                placeholder={is_sending ? "Sending..." : "Type your message"}
                                onChange={(e) => setInput_text(e.target.value)}
                                value={input_text}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        askAI()
                                    }
                                }}
                            />

                            {/* Hidden file input for images */}
                            <input
                                type="file"
                                accept="image/*"
                                ref={input_image_button_ref}
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                        setInput_image(e.target.files[0])
                                    }
                                }}
                            />

                            {/* Send button */}
                            <div
                                className="input-wrapper-input-icon"
                                style={{ marginLeft: '10px', cursor: 'pointer' }}
                                onClick={askAI}
                            >
                                <i className="fa-solid fa-paper-plane"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
